import { db as legacyDb } from '../db';
import {
  type BackupRecording,
  type Recording,
  type Session,
  type SessionChunk,
} from '../types';
import { logger } from '../useSessionRecordingStore/lib/logger';
import { deserializeBlob } from '../utils';
import { dbV2 } from './dbV2';
import { withReopen } from './lifecycle';
import { packBlob, type StoredBlob } from './packBlob';
import { purgeForeignRecordings } from './store';
import {
  chunkPk,
  isSettled,
  recordingPk,
  type BackupBlobRow,
  type BackupRow,
  type ChunkBlobRow,
  type ChunkRow,
  type RecordingRow,
} from './types';
import { assertWriteAllowed, captureEpoch } from './writeFence';

/** Recent recordings without terminal metadata may still be live in another tab. */
const LIVE_RECORDING_GRACE_MS = 24 * 60 * 60 * 1000;

const hasCompleteChunkSequence = (recording: Recording) => {
  if (recording.lastChunkIndex === null) return false;
  const indexes = new Set(recording.chunks.map((chunk) => chunk.index));
  return Array.from({ length: recording.lastChunkIndex + 1 }, (_, index) => index).every(
    (index) => indexes.has(index)
  );
};

const isEligible = (recording: Recording) => {
  // Require all terminal writes before importing a recent recording.
  if (
    recording.endDate &&
    hasCompleteChunkSequence(recording) &&
    recording.backupRecording
  ) {
    return true;
  }
  // After the grace window, import whatever survived a crash.
  const startedAt = new Date(recording.startDate).getTime();
  return Number.isFinite(startedAt)
    ? Date.now() - startedAt > LIVE_RECORDING_GRACE_MS
    : true;
};

const fingerprint = (recording: Recording) =>
  JSON.stringify({
    endDate: recording.endDate,
    lastChunkIndex: recording.lastChunkIndex,
    chunks: recording.chunks.map(({ id, index, isProcessed, size }) => ({
      id,
      index,
      isProcessed,
      size,
    })),
    backup: recording.backupRecording
      ? {
          isProcessed: recording.backupRecording.isProcessed,
          hasBlob: Boolean(recording.backupRecording.blob),
        }
      : null,
  });

interface ImportSummary {
  sessions: number;
  recordings: number;
  discardedLegacyRecordings: number;
  chunks: number;
  pendingChunks: number;
  pendingBackups: number;
  skippedLiveRecordings: number;
  failedSessions: number;
  normalizedCrashedRecordings: number;
  syntheticGapChunks: number;
}

/** Migrates legacy rows owned by currentUserRefId and discards all others. */
export const importLegacyData = async (
  currentUserRefId: string
): Promise<ImportSummary> => {
  const summary: ImportSummary = {
    sessions: 0,
    recordings: 0,
    discardedLegacyRecordings: 0,
    chunks: 0,
    pendingChunks: 0,
    pendingBackups: 0,
    skippedLiveRecordings: 0,
    failedSessions: 0,
    normalizedCrashedRecordings: 0,
    syntheticGapChunks: 0,
  };

  await purgeForeignRecordings(currentUserRefId);

  // Clear legacy canary rows; they contain no recording data.
  try {
    await legacyDb.initialise.clear();
  } catch (err) {
    logger.warn({
      event_type: 'IMPORT_LEGACY_INITIALISE_CLEAR_FAILED',
      details: { error: err instanceof Error ? err.message : String(err) },
    });
  }

  let sessionIds: string[];
  try {
    sessionIds = await legacyDb.sessions.toCollection().primaryKeys();
  } catch (err) {
    logger.warn({
      event_type: 'IMPORT_LEGACY_SCAN_FAILED',
      details: { error: err instanceof Error ? err.message : String(err) },
    });
    return summary;
  }

  for (const sessionId of sessionIds) {
    try {
      const imported = await importSession(sessionId, summary, currentUserRefId);
      if (imported) summary.sessions += 1;
    } catch (err) {
      summary.failedSessions += 1;
      logger.error({
        event_type: 'IMPORT_LEGACY_SESSION_FAILED',
        details: {
          sessionId,
          error: err instanceof Error ? err.message : String(err),
        },
      });
    }
  }

  if (sessionIds.length > 0) {
    logger.info({ event_type: 'IMPORT_LEGACY_DONE', details: { ...summary } });
  }
  return summary;
};

const isSyntheticGapRow = (row: ChunkRow, index: number): boolean =>
  row.syntheticGap === true ||
  (row.id === `import-gap-${index}` && row.status === 'abandoned' && !row.hasBlob);

const importSession = async (
  sessionId: string,
  summary: ImportSummary,
  currentUserRefId: string
) => {
  const epoch = captureEpoch();
  const pruned = await legacyDb.transaction('rw', legacyDb.sessions, async () => {
    assertWriteAllowed(epoch);
    const current = await legacyDb.sessions.get(sessionId);
    if (!current) return { session: undefined, discarded: 0 };
    const ownedRecordings = current.recordings.filter(
      (recording) => recording.userRefId === currentUserRefId
    );
    const discarded = current.recordings.length - ownedRecordings.length;
    if (discarded === 0) return { session: current, discarded };
    if (ownedRecordings.length === 0) {
      await legacyDb.sessions.delete(sessionId);
      return { session: undefined, discarded };
    }
    const ownedSession: Session = { ...current, recordings: ownedRecordings };
    await legacyDb.sessions.put(ownedSession);
    return { session: ownedSession, discarded };
  });
  summary.discardedLegacyRecordings += pruned.discarded;
  const { session } = pruned;
  if (!session) return false;

  const eligible = session.recordings.filter(isEligible);
  const remaining = session.recordings.filter((r) => !isEligible(r));
  summary.skippedLiveRecordings += remaining.length;
  if (eligible.length === 0) return false;

  // Convert blobs before opening the v2 transaction.
  const prepared = await Promise.all(
    eligible.map((recording) => prepareRecording(sessionId, recording, summary))
  );

  // Phase 1 — never-downgrade upsert into v2, one transaction per session.
  await withReopen(() =>
    dbV2.transaction(
      'rw',
      dbV2.recordings,
      dbV2.chunks,
      dbV2.chunkBlobs,
      dbV2.backups,
      dbV2.backupBlobs,
      async () => {
        assertWriteAllowed(epoch);
        for (const p of prepared) {
          const existingRecording = await dbV2.recordings.get(recordingPk(p.recording));
          if (!existingRecording) {
            await dbV2.recordings.put(p.recording);
          } else {
            // Preserve the highest observed index and original owner.
            const prevLast = existingRecording.lastChunkIndex ?? -1;
            const nextLast = p.recording.lastChunkIndex ?? -1;
            if (nextLast > prevLast) {
              await dbV2.recordings.update(recordingPk(p.recording), {
                lastChunkIndex: nextLast,
                ...(existingRecording.endDate ? {} : { endDate: p.recording.endDate }),
              });
            }
          }

          const existingChunks = await dbV2.chunks.bulkGet(
            p.chunks.map((c) => chunkPk(c.row))
          );
          for (let i = 0; i < p.chunks.length; i++) {
            const preparedChunk = p.chunks[i];
            if (!preparedChunk) continue;
            const existing = existingChunks[i];
            // Real chunks may replace settled synthetic gaps; gaps replace nothing.
            if (
              existing &&
              !preparedChunk.synthetic &&
              isSyntheticGapRow(existing, preparedChunk.row.index)
            ) {
              await dbV2.chunks.put(preparedChunk.row);
              if (preparedChunk.blobRow) {
                await dbV2.chunkBlobs.put(preparedChunk.blobRow);
              }
              continue;
            }
            if (existing && isSettled(existing.status)) continue;
            if (existing && preparedChunk.synthetic) continue;
            await dbV2.chunks.put(preparedChunk.row);
            if (preparedChunk.blobRow) await dbV2.chunkBlobs.put(preparedChunk.blobRow);
          }

          if (p.backup) {
            const existingBackup = await dbV2.backups.get(recordingPk(p.backup.row));
            if (!existingBackup || !isSettled(existingBackup.status)) {
              await dbV2.backups.put(p.backup.row);
              if (p.backup.blobRow) await dbV2.backupBlobs.put(p.backup.blobRow);
            }
          }
        }
      }
    )
  );

  // Phase 2 — shrink/delete the legacy row only after v2 committed.
  await legacyDb.transaction('rw', legacyDb.sessions, async () => {
    assertWriteAllowed(epoch);
    const current = await legacyDb.sessions.get(sessionId);
    if (!current) return;
    const preparedById = new Map(prepared.map((p) => [p.recording.recordingId, p]));
    const importedIds = new Set(
      current.recordings
        .filter((recording) => {
          const candidate = preparedById.get(recording.id);
          return (
            candidate &&
            isEligible(recording) &&
            fingerprint(recording) === candidate.sourceFingerprint
          );
        })
        .map((recording) => recording.id)
    );
    const leftover = current.recordings.filter(
      (recording) =>
        recording.userRefId === currentUserRefId && !importedIds.has(recording.id)
    );
    if (leftover.length === 0) {
      await legacyDb.sessions.delete(sessionId);
    } else {
      const shrunk: Session = { ...current, recordings: leftover };
      await legacyDb.sessions.put(shrunk);
    }
  });

  summary.recordings += prepared.length;
  return true;
};

interface PreparedChunk {
  row: ChunkRow;
  blobRow?: ChunkBlobRow;
  /** Gap filler, not real legacy data — must never overwrite an existing row. */
  synthetic?: boolean;
}

interface PreparedRecording {
  recording: RecordingRow;
  sourceFingerprint: string;
  chunks: PreparedChunk[];
  backup?: { row: BackupRow; blobRow?: BackupBlobRow };
}

const prepareRecording = async (
  sessionId: string,
  recording: Recording,
  summary: ImportSummary
): Promise<PreparedRecording> => {
  const startDate = new Date(recording.startDate);

  // Give crashed recordings terminal metadata based on the highest observed index.
  const observedIndexes = recording.chunks.map((chunk) => chunk.index);
  const maxObservedIndex = observedIndexes.length ? Math.max(...observedIndexes) : -1;
  const isCrashed = !recording.endDate || recording.lastChunkIndex === null;
  const lastChunkIndex = recording.lastChunkIndex ?? maxObservedIndex;
  if (isCrashed) summary.normalizedCrashedRecordings += 1;

  const recordingRow: RecordingRow = {
    sessionId,
    recordingId: recording.id,
    userRefId: recording.userRefId!,
    startDate,
    endDate: recording.endDate ? new Date(recording.endDate) : new Date(),
    lastChunkIndex,
    useTranscriptionServiceV2: recording.useTranscriptionServiceV2 ?? false,
    createdAt: Number.isFinite(startDate.getTime()) ? startDate.getTime() : 0,
    importedFromLegacy: true,
    // Only wait for a backup if legacy capture produced one.
    backupRequired: Boolean(recording.backupRecording),
  };

  // Recovery reads `updatedAt` as the owner's last write; an import-time stamp
  // would make every copied row wait out the quiet period.
  const activityAt = new Date(recording.endDate ?? NaN).getTime() || undefined;

  const chunks: PreparedChunk[] = [];
  for (const chunk of recording.chunks) {
    chunks.push(await prepareChunk(sessionId, recording.id, chunk, activityAt, summary));
    summary.chunks += 1;
  }

  // Represent missing indexes as terminal gaps so completion can settle.
  const presentIndexes = new Set(observedIndexes);
  for (let index = 0; index <= lastChunkIndex; index++) {
    if (presentIndexes.has(index)) continue;
    summary.syntheticGapChunks += 1;
    chunks.push({
      synthetic: true,
      row: {
        sessionId,
        recordingId: recording.id,
        index,
        id: `import-gap-${index}`,
        status: 'abandoned',
        wasMissingChunk: true,
        size: 0,
        duration: 0,
        producedAt: 0,
        attempts: 0,
        updatedAt: activityAt ?? Date.now(),
        hasBlob: false,
        syntheticGap: true,
      },
    });
  }

  return {
    recording: recordingRow,
    sourceFingerprint: fingerprint(recording),
    chunks,
    backup: recording.backupRecording
      ? await prepareBackup(sessionId, recording.id, recording.backupRecording, summary)
      : undefined,
  };
};

const prepareChunk = async (
  sessionId: string,
  recordingId: string,
  chunk: SessionChunk,
  activityAt: number | undefined,
  summary: ImportSummary
): Promise<PreparedChunk> => {
  const base = {
    sessionId,
    recordingId,
    index: chunk.index,
    id: chunk.id,
    size: chunk.size,
    duration: chunk.duration,
    producedAt: chunk.producedAt ?? 0,
    wasMissingChunk: chunk.wasMissingChunk ?? false,
    attempts: 0,
    updatedAt: Math.max(chunk.producedAt ?? 0, activityAt ?? 0) || Date.now(),
  };

  if (chunk.isProcessed) {
    // Uploaded already — metadata only; never copy a retained blob forward.
    return { row: { ...base, status: 'uploaded', hasBlob: false } };
  }

  const stored = await toStoredBlob(chunk.blob);
  if (!stored) {
    logger.warn({
      event_type: 'IMPORT_CHUNK_MISSING_BLOB',
      details: { sessionId, recordingId, index: chunk.index },
    });
    return { row: { ...base, status: 'abandoned', hasBlob: false } };
  }

  summary.pendingChunks += 1;
  return {
    row: { ...base, status: 'pending', hasBlob: true },
    blobRow: { sessionId, recordingId, index: chunk.index, blob: stored },
  };
};

const prepareBackup = async (
  sessionId: string,
  recordingId: string,
  backup: BackupRecording,
  summary: ImportSummary
): Promise<PreparedRecording['backup']> => {
  const base = { sessionId, recordingId, attempts: 0, updatedAt: Date.now() };

  if (backup.isProcessed) {
    return { row: { ...base, status: 'uploaded', size: 0, hasBlob: false } };
  }

  const blob = deserializeBlob(backup.blob);
  if (!blob) {
    return { row: { ...base, status: 'abandoned', size: 0, hasBlob: false } };
  }

  summary.pendingBackups += 1;
  return {
    row: { ...base, status: 'pending', size: blob.size, hasBlob: true },
    blobRow: { sessionId, recordingId, blob: await packBlob(blob) },
  };
};

const toStoredBlob = async (
  blob: Blob | string | undefined
): Promise<StoredBlob | undefined> => {
  const deserialized = deserializeBlob(blob);
  if (!deserialized) return undefined;
  return packBlob(deserialized);
};
